import React, { useCallback, useMemo, useState } from 'react';
import { DocumentNode, useQuery } from '@apollo/client';
import getIn from '../connectedForm/utils/getIn';
import BlurTextField from '../connectedForm/BlurTextField';
import EditableDataGrid, { IEditableDataGridProps } from './EditableDataGrid';
import { Direction, IFilterInput, Maybe } from '../../graphql/schema/p4types';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

/*
 * Collection of Sortable Grid components for various uses in the system.
 *  - SortableDataGrid - Provides a UI-sort, note that if the grid is editable this may cause unexpected behaviour.
 *  - QueryGrid - Wraps SortableDataGrid and manages data querying via apollo.
 *  - AbstractLineGrid - For 'AbstractHeader' entities - Allows setting of sort order/direction for the lines on these entities.
 * */

type ISortableDataGridProps<RowDataType = any> = IEditableDataGridProps<RowDataType> & {
  filterable?: boolean;
};

const SortableDataGrid = ({ filterable, ...props }: ISortableDataGridProps) => {
  const [{ direction, column }, setSort] = useState<{ direction: Maybe<Direction | 'NONE'>; column: Maybe<string> }>({
    direction: null,
    column: null,
  });
  const [searchValue, setSearchValue] = useState('');

  const handleGridSort = useCallback(
    (sortColumn: Maybe<string>, sortDirection: Maybe<Direction>) => {
      setSort({ direction: sortDirection, column: sortColumn });
    },
    [setSort]
  );

  const basicComparator = useCallback(
    (a: any, b: any): number => {
      const left = getIn(a, column);
      const right = getIn(b, column);

      if (direction === 'ASC') {
        return left > right ? 1 : -1;
      } else if (direction === 'DESC') {
        return left < right ? 1 : -1;
      }
      return 0;
    },
    [column, direction]
  );

  const searchChange = (value?: string) => {
    setSearchValue(value || '');
  };

  const sortedData = useMemo(() => {
    let shallowSortedData = [...(props.listData || (Array.isArray(props.value) ? props.value : []))];
    if (direction !== 'NONE') {
      shallowSortedData = shallowSortedData.sort(basicComparator);
    }

    if (searchValue && searchValue !== '') {
      shallowSortedData = shallowSortedData.filter((row) => {
        //Include records where any item in the row contains the filter value.
        const columnIndex = props.columns.findIndex((_column) => {
          const value = getIn(row, _column.key);
          if (!value) return false;
          return value.toString().toUpperCase().includes(searchValue.toUpperCase());
        });

        return columnIndex >= 0;
      });
    }

    return shallowSortedData;
  }, [basicComparator, direction, props.columns, props.listData, props.value, searchValue]);

  return (
    <>
      {filterable ? (
        <BlurTextField
          data-testid={'sortable-grid-filter'}
          includeLabel={false}
          value={searchValue}
          onChange={searchChange}
          enableOnChange={true}
          placeholder={'Filter...'}
          styles={{
            input: {
              padding: '10px 40px 10px 10px',
            },
          }}
          clearButton={true}
        />
      ) : null}
      <EditableDataGrid
        {...props}
        listData={sortedData}
        onGridSort={handleGridSort}
        rowsCount={sortedData.length}
        contentHeight={props.contentHeight}
      />
    </>
  );
};

export const QueryGrid = ({
  query,
  filters,
  skip,
  rowClick,
  columnMeta,
}: {
  query: DocumentNode;
  filters: Array<IFilterInput>;
  skip: boolean;
  rowClick: (rowIdx: number, rowData: any, column: any) => void;
  columnMeta: Array<IEditableDataGridColumnDef>;
}) => {
  const gridHeight = window.innerHeight - 330;
  const { data } = useQuery(query, { variables: { filters: filters }, skip, fetchPolicy: 'network-only' });

  let formattedData = [];
  if (data?.listData) {
    formattedData = data.listData.edges.map((edge: any) => edge.node);
  }

  return (
    <SortableDataGrid
      className="entryGrid"
      enableCellSelect={true}
      listData={formattedData}
      columns={columnMeta}
      onRowClick={rowClick}
      contentHeight={gridHeight}
    />
  );
};

//Have to set displayName or things behave incorrectly ;(
SortableDataGrid.displayName = 'EditableDataGrid';
export default SortableDataGrid;
